import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TooltipLine } from '@/utils/charts';
import { ISimpleChartOptions } from '../SimpleChartEngine/types';
import { ChartConfig } from '@/components/ui/chart';
import { Payload, ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';

interface SimpleChartTooltipProps {
  active?: boolean;
  payload?: ReadonlyArray<Payload<ValueType, NameType>>;
  label?: string | number;
  options: ISimpleChartOptions;
  config?: ChartConfig;
  chartContainerRef: React.RefObject<HTMLDivElement>;
  coordinate?: { x?: number; y?: number };
}

export const SimpleChartTooltip: React.FC<SimpleChartTooltipProps> = ({
  active,
  payload,
  label,
  options,
  config,
  chartContainerRef,
  coordinate
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active || !payload || !payload[0] || !chartContainerRef.current) {
      setIsVisible(false);
      return;
    }

    setIsVisible(true);

    // Get the chart container's position
    const chartRect = chartContainerRef.current.getBoundingClientRect();
    
    // Calculate tooltip position
    const tooltipWidth = 240; // Approximate tooltip width
    const tooltipHeight = 100; // Approximate tooltip height
    
    let top, left;
    
    if (coordinate && coordinate.x !== undefined && coordinate.y !== undefined) {
      // Convert chart coordinates to viewport coordinates
      // Recharts coordinates are relative to the chart container
      const mouseX = chartRect.left + coordinate.x;
      const mouseY = chartRect.top + coordinate.y;
      
      // Position tooltip to the right of the mouse by default
      left = mouseX + 15;
      top = mouseY - tooltipHeight / 2;
    } else {
      // Fallback to chart center positioning
      top = chartRect.top + (chartRect.height / 2) - (tooltipHeight / 2);
      left = chartRect.right + 10; // 10px offset from the right edge
    }
    
    // Ensure tooltip doesn't go off screen
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Adjust horizontal position if tooltip goes off screen
    if (left + tooltipWidth > viewportWidth) {
      // Position to the left of the mouse/data point instead
      left = left - tooltipWidth - 30;
    }
    
    // Adjust vertical position if tooltip goes off screen
    if (top + tooltipHeight > viewportHeight) {
      top = viewportHeight - tooltipHeight - 10;
    }
    
    if (top < 10) {
      top = 10;
    }
    
    if (left < 10) {
      left = 10;
    }

    setPosition({ top, left });
  }, [active, payload, chartContainerRef, coordinate]);

  if (!isVisible || !active || !payload || !payload[0]) {
    return null;
  }

  const transformDataKey = (str: string) => 
    str.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className="fixed z-50 rounded-lg border p-2 shadow-lg bg-card-reverse text-card-reverse-foreground min-w-60 text-xs"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        pointerEvents: 'none'
      }}
    >
      <div className="text-sm font-medium mb-2">{label}</div>
      <div className="flex flex-col gap-2">
        {payload.map((item) => (
          <TooltipLine
            key={item?.dataKey as string | number | undefined}
            color={ item?.color?.includes('url') ? item?.stroke : item?.color || item?.payload?.fill}
            label={config?.[item?.dataKey as string]?.label as string || transformDataKey(item?.name as string)}
            value={
              <>
                {options?.tooltipValueFormatter ? options?.tooltipValueFormatter(Number(item?.value)) : item?.value}
              </>
            }
          />
        ))}
      </div>
    </div>
  );

  // Render the tooltip in a portal to the document body
  return createPortal(tooltipContent, document.body);
};
